import * as Dialog from "@radix-ui/react-dialog";
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { type CSSProperties, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import "./date-range-picker.css";

const DAY = 86400000;
const PRESETS = [
  { label: "Past 1 hour", days: 1 / 24 },
  { label: "Past 6 hours", days: 6 / 24 },
  { label: "Past 24 hours", days: 1 },
  { label: "Past 3 days", days: 3 },
  { label: "Past 7 days", days: 7 },
] as const;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const dayKey = (date: Date) => date.toISOString().slice(0, 10);
const parseDay = (day: string) => new Date(`${day}T00:00:00.000Z`);
const monthStart = (date: Date, offset = 0) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1));
const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

interface DateRangePickerProps {
  from: string;
  to: string;
  label: string;
  onChange: (range: {
    from: Date;
    to: Date;
    label: string;
    presetDays: number | null;
  }) => void;
}

/** Selects rolling presets or inclusive UTC calendar dates, capped at now and a 90-day span. Draft dates apply only on confirmation. */
export function DateRangePicker({
  onChange,
  from,
  to,
  label,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(from.slice(0, 10));
  const [end, setEnd] = useState(to.slice(0, 10));
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [datesEdited, setDatesEdited] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const [month, setMonth] = useState(() => monthStart(new Date(to), -1));
  const [position, setPosition] = useState({ left: 16, top: 100 });
  const trigger = useRef<HTMLButtonElement>(null);
  const dates = useRef(new Map<string, HTMLButtonElement>());
  const now = new Date();
  const today = dayKey(now);
  const startDate = parseDay(start);
  const endDate = new Date(
    Math.min(parseDay(end).getTime() + DAY - 1, now.getTime()),
  );
  const span = endDate.getTime() - startDate.getTime();
  const error =
    !start || !end
      ? "Choose a start and end date."
      : !Number.isFinite(span) || span <= 0 || end < start
        ? "End date must be on or after start date."
        : end > today || start > today
          ? "Choose dates up to today."
          : span > 90 * DAY
            ? "Choose a range of 90 days or less."
            : null;
  const previewEnd = selectingEnd ? (hover ?? start) : end;
  const low = start < previewEnd ? start : previewEnd;
  const high = start > previewEnd ? start : previewEnd;

  const setOpened = (value: boolean) => {
    if (value) {
      setDatesEdited(false);
      setStart(from.slice(0, 10));
      setEnd(to.slice(0, 10));
      setSelectingEnd(false);
      setHover(null);
      setMonth(monthStart(new Date(to), -1));
      const rect = trigger.current?.getBoundingClientRect();
      setPosition({
        left: Math.max(16, Math.min(rect?.left ?? 16, window.innerWidth - 780)),
        top: Math.max(
          16,
          Math.min((rect?.bottom ?? 92) + 8, window.innerHeight - 520),
        ),
      });
    }
    setOpen(value);
  };
  const commit = (
    fromDate: Date,
    toDate: Date,
    rangeLabel: string,
    presetDays: number | null = null,
  ) => {
    onChange({ from: fromDate, to: toDate, label: rangeLabel, presetDays });
    setOpen(false);
  };
  const selectDay = (key: string) => {
    setDatesEdited(true);
    if (!selectingEnd) {
      setStart(key);
      setEnd(key);
      setSelectingEnd(true);
    } else {
      setStart(key < start ? key : start);
      setEnd(key > start ? key : start);
      setSelectingEnd(false);
    }
    setHover(null);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpened}>
      <Dialog.Trigger asChild>
        <Button
          ref={trigger}
          variant="outline"
          className="analytics-date-trigger"
          aria-label={`Date range: ${label}`}
        >
          <CalendarDays size={16} />
          <span>{label}</span>
          <ChevronDown size={14} />
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="analytics-date-overlay" />
        <Dialog.Content
          className="analytics-date-picker"
          style={
            {
              "--picker-left": `${position.left}px`,
              "--picker-top": `${position.top}px`,
            } as CSSProperties
          }
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">Select date range</Dialog.Title>
          <div className="analytics-date-body">
            <nav
              className="analytics-date-shortcuts"
              aria-label="Quick date ranges"
            >
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  aria-pressed={label === preset.label}
                  onClick={() => {
                    const end = new Date();
                    commit(
                      new Date(end.getTime() - preset.days * DAY),
                      end,
                      preset.label,
                      preset.days,
                    );
                  }}
                >
                  <span>{preset.label}</span>
                  {label === preset.label && <Check size={14} />}
                </button>
              ))}
            </nav>
            <div className="analytics-date-calendar-area">
              <div className="analytics-date-months">
                {[0, 1].map((offset) => {
                  const date = monthStart(month, offset);
                  const title = date.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                    timeZone: "UTC",
                  });
                  const count = new Date(
                    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
                  ).getUTCDate();
                  return (
                    <section
                      className="analytics-date-month"
                      key={date.toISOString()}
                      aria-label={title}
                    >
                      <header>
                        {offset === 0 ? (
                          <button
                            type="button"
                            aria-label="Previous month"
                            onClick={() => setMonth(monthStart(month, -1))}
                          >
                            <ChevronLeft size={18} />
                          </button>
                        ) : (
                          <span />
                        )}
                        <h3>{title}</h3>
                        {offset === 1 ? (
                          <button
                            type="button"
                            aria-label="Next month"
                            disabled={
                              monthStart(month, 1).getTime() >=
                              monthStart(now).getTime()
                            }
                            onClick={() => setMonth(monthStart(month, 1))}
                          >
                            <ChevronRight size={18} />
                          </button>
                        ) : (
                          <span />
                        )}
                      </header>
                      <div className="analytics-date-grid">
                        {WEEKDAYS.map((name) => (
                          <span
                            key={name}
                            className="analytics-date-weekday"
                            aria-hidden="true"
                          >
                            {name}
                          </span>
                        ))}
                        {Array.from({ length: date.getUTCDay() }, (_, i) => (
                          <span key={`blank-${i}`} />
                        ))}
                        {Array.from({ length: count }, (_, i) => {
                          const day = new Date(
                            Date.UTC(
                              date.getUTCFullYear(),
                              date.getUTCMonth(),
                              i + 1,
                            ),
                          );
                          const key = dayKey(day);
                          const boundary = key === start || key === previewEnd;
                          return (
                            <button
                              key={key}
                              ref={(element) => {
                                if (element) dates.current.set(key, element);
                                else dates.current.delete(key);
                              }}
                              type="button"
                              aria-label={formatDate(day)}
                              aria-pressed={key >= low && key <= high}
                              aria-current={key === today ? "date" : undefined}
                              disabled={key > today}
                              data-boundary={boundary || undefined}
                              data-in-range={
                                (key >= low && key <= high) || undefined
                              }
                              onClick={() => selectDay(key)}
                              onMouseEnter={() => {
                                if (selectingEnd) setHover(key);
                              }}
                              onMouseLeave={() => setHover(null)}
                              onKeyDown={(event) => {
                                const shift = {
                                  ArrowLeft: -1,
                                  ArrowRight: 1,
                                  ArrowUp: -7,
                                  ArrowDown: 7,
                                  Home: -day.getUTCDay(),
                                  End: 6 - day.getUTCDay(),
                                }[event.key];
                                if (shift === undefined) return;
                                event.preventDefault();
                                const target = dayKey(
                                  new Date(day.getTime() + shift * DAY),
                                );
                                const button = dates.current.get(target);
                                if (button && !button.disabled) button.focus();
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
              <div className="analytics-date-inputs">
                <label>
                  Start date
                  <input
                    type="date"
                    value={start}
                    max={today}
                    onChange={(event) => {
                      setDatesEdited(true);
                      setStart(event.target.value);
                      setSelectingEnd(false);
                    }}
                  />
                </label>
                <span aria-hidden="true">–</span>
                <label>
                  End date
                  <input
                    type="date"
                    value={end}
                    max={today}
                    onChange={(event) => {
                      setDatesEdited(true);
                      setEnd(event.target.value);
                      setSelectingEnd(false);
                    }}
                  />
                </label>
                <span className="analytics-date-timezone">UTC</span>
              </div>
              {error && (
                <p className="analytics-date-error" role="alert">
                  {error}
                </p>
              )}
            </div>
          </div>
          <footer className="analytics-date-footer">
            <Dialog.Close asChild>
              <Button variant="ghost">Cancel</Button>
            </Dialog.Close>
            <Button
              disabled={Boolean(error)}
              onClick={() =>
                datesEdited
                  ? commit(
                      startDate,
                      endDate,
                      `${formatDate(startDate)} – ${formatDate(endDate)}`,
                    )
                  : commit(
                      new Date(from),
                      new Date(to),
                      label,
                      PRESETS.find((preset) => preset.label === label)?.days ??
                        null,
                    )
              }
            >
              Apply
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
